import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '@/utils/api';
import { loadAllPages } from '@/utils/loadAllPages';

import { Contact, Account, Product } from './contactListTypes';
export function useContactsController() {
const navigate = useNavigate();
const location = useLocation();
const { user } = useAuthStore();
const crm = user?.role?.name === 'operations_manager';
const [contacts, setContacts] = useState<Contact[]>([]);
const [accounts, setAccounts] = useState<Account[]>([]);
const [accountsError, setAccountsError] = useState<string | null>(null);
const [products, setProducts] = useState<Product[]>([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState<string | null>(null);
const [page, setPage] = useState(1);
const [total, setTotal] = useState(0);
const limit = 20;
const totalPages = Math.ceil(total / limit);
const [search, setSearch] = useState(location.state?.search ?? '');
const [filterStatus, setFilterStatus] = useState('');
const [filterSource, setFilterSource] = useState('');
const [filterCustomerType, setFilterCustomerType] = useState('');
const [showFilters, setShowFilters] = useState(false);
const [sortField, setSortField] = useState<string>('createdAt');
const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
const [showModal, setShowModal] = useState(location.state?.action === 'create');
const [saving, setSaving] = useState(false);
const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', title: '',
    accountId: '', source: '', tags: '',
    productInterest: '', notes: '', customerType: 'b2c' as string,
    street: '', city: '', state: '', pincode: '', gstin: '',
  });
const [gstFetching, setGstFetching] = useState(false);
const [gstFetched, setGstFetched] = useState(false);
const [gstCompanyName, setGstCompanyName] = useState('');
const [gstEnrichment, setGstEnrichment] = useState<any>(null);
const handleGstLookup = async (gstin: string) => {
    if (gstin.length !== 15) return;
    setGstFetching(true);
    try {
      const res = await api.get(`/accounts/gst-lookup/${gstin}`);
      const d = res.data.data;
      if (d.companyName || d.tradeName || d.legalName) {
        setGstCompanyName(d.companyName || d.tradeName || d.legalName);
        setGstFetched(true);
        setGstEnrichment({
          tradeName: d.tradeName || null,
          legalName: d.legalName || null,
          gstStatus: d.gstStatus || null,
          gstRegistrationDate: d.registrationDate || null,
          gstBusinessType: d.businessType || null,
          gstLastUpdated: d.lastUpdated || null,
          gstRawData: d.rawData || null,
          stateCode: d.stateCode || null,
          pan: d.pan || null,
          gstAddress: d.address || null,
        });
        // Auto-fill state from GST
        if (d.stateName && !form.state) {
          setForm(f => ({ ...f, state: d.stateName }));
        }
      }
    } catch { /* ignore — offline fallback will be used */ }
    setGstFetching(false);
  };
const [showEditModal, setShowEditModal] = useState(false);
const [editId, setEditId] = useState<string | null>(null);
const [editForm, setEditForm] = useState({
    nature: '', firstName: '', lastName: '', email: '', phone: '', title: '',
    accountId: '', source: '', tags: '',
    productInterest: '', notes: '', customerType: 'b2c' as string,
    street: '', city: '', state: '', pincode: '',
  });
const [editSaving, setEditSaving] = useState(false);
const [deleteId, setDeleteId] = useState<string | null>(null);
const [deleteName, setDeleteName] = useState('');
const [deleting, setDeleting] = useState(false);
const [showImportModal, setShowImportModal] = useState(false);
const [importFile, setImportFile] = useState<File | null>(null);
const [importPreview, setImportPreview] = useState<Record<string, string>[]>([]);
const [importAllRows, setImportAllRows] = useState<Record<string, string>[]>([]);
const [importing, setImporting] = useState(false);
const [importResult, setImportResult] = useState<{ success: number; errors: number } | null>(null);
const summaryStats = useMemo(() => {
    // BUG-001 fix: use pagination total from API, not current page size
    const pageSize = contacts.length;
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const newThisMonth = contacts.filter(c => new Date(c.createdAt) >= firstOfMonth).length;

    // Source breakdown
    const sourceCounts: Record<string, number> = {};
    contacts.forEach(c => {
      const src = c.source || 'Unknown';
      sourceCounts[src] = (sourceCounts[src] || 0) + 1;
    });
    const topSources = Object.entries(sourceCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    const avgLeadScore = pageSize > 0
      ? Math.round(contacts.reduce((sum, c) => sum + (c.leadScore ?? 0), 0) / pageSize)
      : 0;

    return { total, newThisMonth, topSources, avgLeadScore };
  }, [contacts, total]);
const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {
        page: String(page),
        limit: String(limit),
      };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterSource) params.source = filterSource;
      if (filterCustomerType) params.customerType = filterCustomerType;
      if (sortField) params.sortBy = sortField;
      if (sortDir) params.sortOrder = sortDir;
      const res = await api.get('/contacts', { params });
      setContacts(res.data.data?.contacts ?? res.data.data ?? []);
      setTotal(res.data.data?.pagination?.total ?? res.data.pagination?.total ?? res.data.total ?? 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterSource, filterCustomerType, sortField, sortDir, page]);
useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);
useEffect(() => {
    // SUN-283 item 7: every account, page by page (a bare request sends only the first 20); a failure is shown.
    const ctl = new AbortController();
    loadAllPages<Account>('/accounts', ctl.signal, { label: 'Account' })
      .then(rows => { if (!ctl.signal.aborted) { setAccounts(rows); setAccountsError(null); } })
      .catch((err: any) => {
        if (ctl.signal.aborted) return;
        setAccountsError(`Could not load accounts (${err?.response?.data?.error || err?.message || 'unknown error'}). Reload the page to try again.`);
      });
    api.get('/products').then(res => {
      setProducts(res.data.data?.products ?? res.data.data ?? []);
    }).catch(() => {});
    return () => ctl.abort();
  }, []);
const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };
const handleCreate = async () => {
    try {
      setSaving(true);
      const { street, city, state, pincode, productInterest, notes, gstin, ...rest } = form;
      const address = (street || city || state || pincode)
        ? { street, city, state, pincode }
        : undefined;
      await api.post('/contacts', {
        ...rest,
        accountId: form.accountId || undefined,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()) : [],
        address,
        productInterest: productInterest || undefined,
        city: city || undefined,
        state: state || undefined,
        notes: notes || undefined,
        gstin: gstin || undefined,
        // Pass company name + enrichment so backend can auto-create Account
        companyName: gstCompanyName || undefined,
        ...(gstEnrichment || {}),
      });
      setShowModal(false);
      setForm({ firstName: '', lastName: '', email: '', phone: '', title: '', accountId: '', source: '', tags: '', productInterest: '', notes: '', customerType: 'b2c', street: '', city: '', state: '', pincode: '', gstin: '' });
      setGstFetched(false); setGstCompanyName(''); setGstEnrichment(null);
      fetchContacts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };
const openEdit = (c: Contact, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditId(c.id);
    const addr = (c as any).address as { street?: string; city?: string; state?: string; pincode?: string } | null;
    setEditForm({
      nature: c.nature ?? '', firstName: c.firstName, lastName: c.lastName, email: c.email || '',
      phone: c.phone || '', title: c.title || '',
      accountId: c.account?.id || '', source: c.source || '', tags: '',
      productInterest: c.productInterest || '', notes: c.notes || '', customerType: (c as any).customerType || 'b2c',
      street: addr?.street || '', city: addr?.city || c.city || '', state: addr?.state || c.state || '', pincode: addr?.pincode || '',
    });
    setShowEditModal(true);
  };
useEffect(() => {
    const id = location.state?.recordId;
    if (!crm || typeof id !== 'string') return;
    let current = true;
    api.get(`/contacts/${encodeURIComponent(id)}`).then(response => {
      if (current) openEdit(response.data.data);
    }).catch(() => { if (current) setError('Could not open the contact for correction.'); });
    return () => { current = false; };
  }, [crm, location.state?.recordId]);
const handleEdit = async () => {
    if (!editId) return;
    try {
      setEditSaving(true);
      const { street, city, state, pincode, productInterest, notes, ...editRest } = editForm;
      const editAddress = (street || city || state || pincode)
        ? { street, city, state, pincode }
        : undefined;
      await api.put(`/contacts/${editId}`, {
        ...editRest,
        nature: editForm.nature || null,
        accountId: editForm.accountId || undefined,
        tags: editForm.tags ? editForm.tags.split(',').map(t => t.trim()) : undefined,
        address: editAddress,
        productInterest: productInterest || undefined,
        city: city || undefined,
        state: state || undefined,
        notes: notes || undefined,
      });
      setShowEditModal(false);
      setEditId(null);
      fetchContacts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update contact');
    } finally {
      setEditSaving(false);
    }
  };
const openDelete = (c: Contact, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteId(c.id);
    setDeleteName(`${joinPersonName(c.firstName, c.lastName)}`);
  };
const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await api.delete(`/contacts/${deleteId}`);
      setDeleteId(null);
      fetchContacts();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete contact');
    } finally {
      setDeleting(false);
    }
  };
const handleCsvFileChange = (file: File | null) => {
    setImportFile(file);
    setImportResult(null);
    if (!file) { setImportPreview([]); setImportAllRows([]); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { setImportPreview([]); setImportAllRows([]); return; }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
      const rows = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
        const row: Record<string, string> = {};
        headers.forEach((h, i) => { row[h] = vals[i] || ''; });
        return row;
      });
      setImportAllRows(rows);
      setImportPreview(rows.slice(0, 5));
    };
    reader.readAsText(file);
  };
const handleImport = async () => {
    if (importAllRows.length === 0) return;
    setImporting(true);
    setImportResult(null);
    try {
      const FIELD_MAP: Record<string, string> = {
        firstname: 'firstName', first_name: 'firstName', 'first name': 'firstName',
        lastname: 'lastName', last_name: 'lastName', 'last name': 'lastName',
        email: 'email', phone: 'phone', title: 'title', source: 'source',
      };
      const mapped = importAllRows.map(row => {
        const contact: Record<string, string> = {};
        Object.entries(row).forEach(([key, val]) => {
          const mapped_key = FIELD_MAP[key.toLowerCase()] || key;
          contact[mapped_key] = val;
        });
        return contact;
      }).filter(c => c.firstName && c.lastName);

      const res = await api.post('/contacts/import', { contacts: mapped });
      const count = res.data.data?.count ?? mapped.length;
      setImportResult({ success: count, errors: importAllRows.length - mapped.length });
      fetchContacts();
    } catch (err: any) {
      setImportResult({ success: 0, errors: importAllRows.length });
      alert(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };
return { crm, navigate, contacts, setContacts, accounts, setAccounts, accountsError, products, setProducts, loading, setLoading, error, setError, page, setPage, total, setTotal, limit, totalPages, search, setSearch, filterStatus, setFilterStatus, filterSource, setFilterSource, filterCustomerType, setFilterCustomerType, showFilters, setShowFilters, sortField, setSortField, sortDir, setSortDir, showModal, setShowModal, saving, setSaving, form, setForm, gstFetching, setGstFetching, gstFetched, setGstFetched, gstCompanyName, setGstCompanyName, gstEnrichment, setGstEnrichment, handleGstLookup, showEditModal, setShowEditModal, editId, setEditId, editForm, setEditForm, editSaving, setEditSaving, deleteId, setDeleteId, deleteName, setDeleteName, deleting, setDeleting, showImportModal, setShowImportModal, importFile, setImportFile, importPreview, setImportPreview, importAllRows, setImportAllRows, importing, setImporting, importResult, setImportResult, summaryStats, fetchContacts, handleSort, SortIcon, handleCreate, openEdit, handleEdit, openDelete, handleDelete, handleCsvFileChange, handleImport };
}
